import BodyText from '@lyra/core/components/BodyText'
import Section from '@lyra/core/components/Section'
import Table, { createColumnHelper, TableData } from '@lyra/core/components/Table'
import formatDuration from '@lyra/core/utils/formatDuration'
import { PendingUnstake } from '@lyra/web/constants/drv'
import { TokenId } from '@lyra/web/constants/tokens'
import { TransactionOptions } from '@lyra/web/constants/transactions'
import useLyraFundingBalances from '@lyra/web/hooks/useLyraFundingBalances'
import useOrderbookTimestamp from '@lyra/web/hooks/useOrderbookTimestamp'
import usePendingUnstakes from '@lyra/web/hooks/usePendingUnstakes'
import useTransaction from '@lyra/web/hooks/useTransaction'
import { formatTokenBalance } from '@lyra/web/utils/tokens'
import { useCallback, useMemo } from 'react'
import { XStack } from 'tamagui'

import TransactionButtonRow from '../common/TransactionButtonRow'

export default function DrvPendingUnstakeSectionRows() {
  const { data: pendingUnstakes, isLoading, mutate: mutatePendingUnstakes } = usePendingUnstakes()
  const { mutate: mutateBalances } = useLyraFundingBalances()

  const { cancelUnstake, finalizeUnstake } = useTransaction()
  const { getUtcTimestampSec } = useOrderbookTimestamp()
  const nowSec = useMemo(() => getUtcTimestampSec(), [getUtcTimestampSec])

  const columnHelper = createColumnHelper<TableData<PendingUnstake>>()

  const handlePressCancelUnstake = useCallback(
    async (pendingUnstake: PendingUnstake, options: TransactionOptions) => {
      await cancelUnstake(pendingUnstake, options)
      await Promise.all([mutatePendingUnstakes(), mutateBalances()])
    },
    [cancelUnstake, mutatePendingUnstakes, mutateBalances]
  )

  const handlePressClaimUnstake = useCallback(
    async (pendingUnstake: PendingUnstake, options: TransactionOptions) => {
      await finalizeUnstake(pendingUnstake, options)
      await Promise.all([mutatePendingUnstakes(), mutateBalances()])
    },
    [finalizeUnstake, mutatePendingUnstakes, mutateBalances]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('amount', {
        header: 'Unstake Amount',
        cell: ({
          row: {
            original: { drvAmount },
          },
        }) => <BodyText>{formatTokenBalance(drvAmount, TokenId.DRV)}</BodyText>,
      }),
      columnHelper.accessor('timeRemaining', {
        header: 'Time To Unstake',
        cell: ({
          row: {
            original: { endTimestamp },
          },
        }) => {
          if (nowSec > endTimestamp) {
            return <BodyText>Complete</BodyText>
          }
          return (
            <BodyText>
              {formatDuration(Math.max(endTimestamp - nowSec, 0), { showSeconds: false })}
            </BodyText>
          )
        },
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        meta: {
          justifyContent: 'flex-end',
          isActionColumn: true,
          minWidth: 140,
          maxWidth: 140,
        },
        cell: ({ row: { original } }) => {
          return (
            <XStack gap="$1">
              {nowSec > original.endTimestamp ? (
                <TransactionButtonRow
                  reviewTitle="Complete Unstake"
                  loggerAction="finalize-unstake"
                  label="Complete Unstake"
                  onSubmitTransaction={async (options) =>
                    handlePressClaimUnstake(original, options)
                  }
                  paddingHorizontal={0}
                  buttonProps={{
                    size: 'md',
                    color: 'primary',
                    isSolid: false,
                  }}
                />
              ) : (
                <TransactionButtonRow
                  reviewTitle="Cancel Unstake"
                  loggerAction="cancel-unstake"
                  label="Cancel"
                  onSubmitTransaction={async (options) =>
                    handlePressCancelUnstake(original, options)
                  }
                  paddingHorizontal={0}
                  buttonProps={{
                    size: 'md',
                    color: 'primary',
                    isSolid: false,
                  }}
                />
              )}
            </XStack>
          )
        },
      }),
    ],
    [columnHelper, handlePressCancelUnstake, handlePressClaimUnstake, nowSec]
  )

  return !isLoading && pendingUnstakes?.length ? (
    <>
      <Section.Separator />
      <Section.Subheader title="Pending Unstakes" />
      <Section.Table>
        <Table columns={columns} data={pendingUnstakes} />
      </Section.Table>
    </>
  ) : null
}
